import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import { setMemoryToken, clearMemoryToken } from '../api/api';

const AuthContext = createContext();

const API = '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deviceAuthorized, setDeviceAuthorized] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Server may already have cleared the session
    }
    clearMemoryToken();
    setUser(null);
    setDeviceAuthorized(true);
    setSessionExpired(false);
  }, []);

  // Listen for token expiration events from api.js interceptor
  // Only show "session expired" if user was actually logged in
  useEffect(() => {
    const handleLogout = () => {
      if (user) {
        setSessionExpired(true);
      }
      clearMemoryToken();
      setUser(null);
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [user]);

  // Check existing session on mount via httpOnly cookie + memory token
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setDeviceAuthorized(true);
      } catch (error) {
        if (error.response?.status === 403) {
          setDeviceAuthorized(false);
        }
        // No session found - that's OK, just show login
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  const login = async (username, password) => {
    try {
      const deviceData = await getDeviceFingerprint();
      
      const loginPayload = {
        username,
        password,
        device_fingerprint: deviceData.fingerprint,
        device_name: deviceData.deviceName,
        device_info: deviceData.deviceInfo
      };
      
      const response = await axios.post(`${API}/auth/login`, loginPayload, { withCredentials: true });
      
      const { access_token, user: userData, device_authorized } = response.data;
      
      // Store token in memory as fallback for iframe/cross-origin contexts
      if (access_token) {
        setMemoryToken(access_token);
      }
      
      setUser(userData);
      setDeviceAuthorized(device_authorized);
      setSessionExpired(false);
      sessionStorage.removeItem('pendingLogin');
      return { success: true, deviceAuthorized: device_authorized };
    } catch (error) {
      if (error.response?.status === 403) {
        const deviceData = await getDeviceFingerprint();
        sessionStorage.setItem('pendingLogin', JSON.stringify({
          username,
          password,
          device_fingerprint: deviceData.fingerprint,
          device_name: deviceData.deviceName,
          device_info: deviceData.deviceInfo
        }));
        
        return { 
          success: false, 
          deviceNotAuthorized: true,
          error: error.response?.data?.detail || 'Dispositivo no autorizado'
        };
      }
      let errorMsg = 'Error al iniciar sesión';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMsg = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        errorMsg = 'No se pudo conectar con el servidor. Verifica tu conexión o contacta al administrador.';
      } else if (error.response?.status) {
        errorMsg = `Error del servidor (${error.response.status}). Intenta de nuevo.`;
      }
      return { 
        success: false, 
        deviceNotAuthorized: false,
        error: errorMsg
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, deviceAuthorized, sessionExpired, setSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
